import { useRef, useState } from 'react';
import { useIntersectionObserver } from 'usehooks-ts';

import { asIntersectionObserverRef } from '../../utils/hooks';
import ShimmerPlaceholder from '../ShimmerPlaceHolder/ShimmerPlaceHolder';
import Picture from '../Picture/Picture';

import styles from './LazyLoadImage.module.css';

type Option = 'none' | 'blur' | 'grayscale';

interface Props {
  src: string;
  alt: string;
  width?: number | string;
  height?: number | string;
  option?: Option;
  threshold?: number;
}

export default function LazyLoadImage({
  src,
  alt,
  width,
  height,
  option = 'none',
  threshold = 0.5,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);
  const entry = useIntersectionObserver(asIntersectionObserverRef(ref), {
    root: null,
    rootMargin: '0px',
    threshold,
    freezeOnceVisible: true,
  });
  const isVisible = !!entry?.isIntersecting;
  const onLoad = () => setLoaded(true);

  let filter: string | undefined;
  if (option === 'blur') {
    filter = `blur(${loaded ? '0' : '10px'})`;
  } else if (option === 'grayscale') {
    filter = `grayscale(${loaded ? '0%' : '100%'})`;
  }

  return (
    <div>
      <ShimmerPlaceholder
        ref={ref}
        width={width}
        height={height}
        style={{ display: loaded ? 'none' : 'block' }}
      />
      {isVisible && (
        <Picture
          className={styles.picture}
          style={{
            visibility: isVisible ? 'visible' : 'hidden',
            filter,
          }}
          src={src}
          alt={alt}
          onLoad={onLoad}
        />
      )}
    </div>
  );
}
